import { getValueUnitDivisor } from "../../../utilities/UnitsAndKeys/convertUnit";
import { getDataKeyName } from "../../../utilities/UnitsAndKeys/getDataKeyName";
import { getUnitColor } from "../../../utilities/UnitsAndKeys/getUnitColor";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import { formatHeatmapData } from "./Heatmap.utils";
import React, { useEffect, useRef } from "react";
import { lighten } from "@material-ui/core";
import { parse, format } from "date-fns";

am4core.useTheme(am4themes_animated);

export const AmChartHeatmap = ({
  data,
  dataKey,
  height = 500,
  graphId,
  handleHeatmapClick,
}) => {
  const chart = useRef(null);

  useEffect(() => {
    chart.current = am4core.create(
      `calendar-heatmap${graphId}`,
      am4charts.XYChart
    );
    return () => chart.current.dispose();
  }, [dataKey, graphId]);

  useEffect(() => {
    if (chart.current) {
      const grey = "#808080";
      const { unit, divisor } = getValueUnitDivisor(
        data.totals[dataKey] / data.data.length,
        "energy"
      );

      chart.current.dispose();
      chart.current = am4core.create(
        `calendar-heatmap${graphId}`,
        am4charts.XYChart
      );

      chart.current.maskBullets = false;

      var xAxis = chart.current.xAxes.push(new am4charts.CategoryAxis());
      var yAxis = chart.current.yAxes.push(new am4charts.CategoryAxis());

      xAxis.dataFields.category = "weekday";
      yAxis.dataFields.category = "month";
      yAxis.renderer.labels.template.fill = am4core.color(grey);
      yAxis.renderer.labels.template.fontSize = 12;

      xAxis.renderer.grid.template.disabled = true;
      xAxis.renderer.minGridDistance = 20;
      xAxis.title.text = `[bold]${getDataKeyName(dataKey)} (${unit})[/]`;
      xAxis.title.paddingBottom = 10;
      xAxis.title.paddingTop = 20;
      xAxis.title.fill = am4core.color(grey);
      xAxis.renderer.labels.template.fill = am4core.color(grey);
      xAxis.renderer.labels.template.fontSize = 10;

      // yAxis.renderer.inversed = true;
      yAxis.renderer.grid.template.disabled = true;
      yAxis.renderer.minGridDistance = 20;

      var series = chart.current.series.push(new am4charts.ColumnSeries());
      series.dataFields.categoryX = "weekday";
      series.dataFields.categoryY = "month";
      series.dataFields.value = dataKey;
      series.sequencedInterpolation = true;
      series.defaultState.transitionDuration = 3000;
      if (series.tooltip) {
        series.tooltip.getFillFromObject = false;
        series.tooltip.pointerOrientation = "vertical";
        series.tooltip.background.fill = am4core.color(getUnitColor(dataKey));
      }

      // click to go to day
      if (handleHeatmapClick) {
        series.cursorOverStyle = am4core.MouseCursorStyle.pointer;
        series.columns.template.events.on("hit", (ev) => {
          const dataItem = ev.target.dataItem;
          const day = dataItem.categoryX;
          const monthYear = dataItem.categoryY;
          const date = parse(`${day} ${monthYear}`, "dd MMM yyyy", new Date());
          handleHeatmapClick(date);
        });
      }

      // var bgColor = new am4core.InterfaceColorSet().getFor("background");

      var columnTemplate = series.columns.template;
      columnTemplate.strokeWidth = 0;
      columnTemplate.strokeOpacity = 0.2;
      columnTemplate.width = am4core.percent(100);
      columnTemplate.height = am4core.percent(100);
      columnTemplate.adapter.add("tooltipHTML", (tooltipHTML, target) => {
        const dataItem = target.dataItem;
        const dayNo = dataItem.categories.categoryX;
        const monthAndYear = dataItem.categories.categoryY;
        const date = format(
          parse(`${dayNo} ${monthAndYear}`, "d MMM yyyy", new Date()),
          "EEE d MMM yyyy"
        );
        return `<strong>${getDataKeyName(
          dataKey
        )}</strong><br />${date}<br /> {value.workingValue.formatNumber('#.')} ${unit}`;
      });

      series.heatRules.push({
        target: columnTemplate,
        property: "fill",
        min: am4core.color(lighten(getUnitColor(dataKey), 0.95)),
        max: data.totals[dataKey]
          ? am4core.color(getUnitColor(dataKey))
          : am4core.color(lighten(getUnitColor(dataKey), 0.9)), // Override max color if there's no data
      });

      // heat legend
      var heatLegend = chart.current.bottomAxesContainer.createChild(
        am4charts.HeatLegend
      );
      heatLegend.width = am4core.percent(100);
      heatLegend.series = series;
      heatLegend.valueAxis.renderer.labels.template.fontSize = 9;
      heatLegend.valueAxis.renderer.minGridDistance = 30;
      heatLegend.valueAxis.renderer.labels.template.fill = am4core.color(grey);

      // heat legend behavior
      series.columns.template.events.on("over", function(event) {
        handleHover(event.target);
      });

      series.columns.template.events.on("hit", function(event) {
        handleHover(event.target);
      });

      function handleHover(column) {
        if (!isNaN(column.dataItem.value) && column) {
          heatLegend.valueAxis.showTooltipAt(column.dataItem.value);
          column.strokeWidth = 3;
          column.zIndex = 1000;
          column.strokeOpacity = 0.6;
          column.stroke = am4core.color("#FFF");
        } else {
          heatLegend.valueAxis.hideTooltip();
        }
      }

      series.columns.template.events.on("out", function(event) {
        heatLegend.valueAxis.hideTooltip();
        const column = event.target;
        if (column) {
          column.strokeWidth = 0;
          column.zIndex = 1;
        }
      });

      chart.current.data = formatHeatmapData(data, dataKey, unit, divisor);
    }
  }, [data, dataKey, graphId, handleHeatmapClick]);

  return <div style={{ height: height }} id={`calendar-heatmap${graphId}`} />;
};

export default AmChartHeatmap;



// WEBPACK FOOTER //
// ./resources/assets/js/app/SchoolProfileView/components/Heatmap/HeatmapChart.js